import { useState, useCallback } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { WorkspaceProvider, useWorkspace } from "@/contexts/WorkspaceContext";
import { TopNav } from "@/components/TopNav";
import { FloatingAIChat } from "@/components/FloatingAIChat";
import { FloatingCalendar } from "@/components/FloatingCalendar";
import { useUserRole } from "@/hooks/useUserRole";
import { TrialBanner } from "@/components/TrialBanner";
import { MobileTabBar } from "@/components/MobileTabBar";
import { WorkspaceToggle } from "@/components/WorkspaceToggle";
import { useIsMobile } from "@/hooks/use-mobile";
import { Radio } from "lucide-react";

interface AIResult {
  leadId: string;
  leadName: string;
  action: 'created' | 'updated';
  hasGanttChanges: boolean;
}

const DashboardContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspace, selectedGroupId } = useWorkspace();
  const { isTrial, isExpired, loading: roleLoading } = useUserRole();
  const [highlightedLeadId, setHighlightedLeadId] = useState<string | null>(null);
  const [openLeadDetails, setOpenLeadDetails] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const handleNavigateToLead = useCallback((result: AIResult) => {
    // Navigate to the correct page based on workspace
    const basePath = workspace === 'groups' && selectedGroupId 
      ? `/dashboard/groups/${selectedGroupId}/hot`
      : '/dashboard/personal/hot';
    
    // If not already on the hot leads page, navigate there
    if (!location.pathname.includes('/hot')) {
      navigate(basePath);
    }
    
    // Set highlighted lead for table scroll/highlight
    setHighlightedLeadId(result.leadId);
    
    // Always open the lead details dialog when AI makes changes
    setTimeout(() => setOpenLeadDetails(result.leadId), 500);
    
    // Clear highlight after animation
    setTimeout(() => setHighlightedLeadId(null), 3000);
  }, [workspace, selectedGroupId, location.pathname, navigate]);

  const handleLeadChange = useCallback(() => {
    // Trigger a custom event that lead pages can listen to
    window.dispatchEvent(new CustomEvent('ai-lead-change'));
  }, []);

  return (
    <div className="min-h-screen flex flex-col w-full bg-background">
      {isMobile ? (
        <header className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-30 pt-safe">
          <div className="px-4 flex items-center justify-between gap-3 h-14">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 shrink-0"
              aria-label="Home"
            >
              <Radio className="h-5 w-5 text-primary" />
              <span className="font-display text-base font-semibold tracking-tight">EchoCRM</span>
            </button>
            <WorkspaceToggle />
          </div>
        </header>
      ) : (
        <TopNav />
      )}
      <main className="flex-1 overflow-auto flex flex-col">
          {/* Trial / expired-plan banner */}
          {!roleLoading && (isTrial || isExpired) && (
            <div className="px-4 pt-4">
              <TrialBanner />
            </div>
          )}
          <div className={`flex-1 ${isMobile ? 'pb-[calc(4rem+env(safe-area-inset-bottom))]' : ''}`}>
            <Outlet context={{ highlightedLeadId, openLeadDetails, setOpenLeadDetails }} />
          </div>
          <FloatingCalendar onOpenLead={(leadId) => setOpenLeadDetails(leadId)} />
          <FloatingAIChat onLeadChange={handleLeadChange} onNavigateToLead={handleNavigateToLead} />
          {!isMobile && (
            <footer className="py-1 px-4 border-t border-border bg-background">
              <div className="flex items-center justify-center font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
                Support: <a href="mailto:support@echocrm.app" className="ml-1 text-primary hover:underline">support@echocrm.app</a>
              </div>
            </footer>
          )}
        <MobileTabBar />
      </main>
    </div>
  );
};

const DashboardLayout = () => {
  return (
    <WorkspaceProvider>
      <DashboardContent />
    </WorkspaceProvider>
  );
};

export default DashboardLayout;